import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  useTheme,
  CircularProgress,
  Checkbox,
  FormGroup
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Money as CashIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  CheckCircle as SuccessIcon,
  Print as PrintIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  RequestQuote as ChequeIcon
} from '@mui/icons-material';

/**
 * Payment processing component for the POS system
 * @param {Object} props Component props
 * @param {Object} props.totals Cart totals object (subtotal, taxAmount, total)
 * @param {Object} props.customer Selected customer object (optional)
 * @param {Function} props.onCompletePayment Callback when payment is completed
 * @param {boolean} props.loading Whether payment is being processed
 * @param {boolean} props.saleCompleted Whether sale has been completed
 * @param {Function} props.onPrintReceipt Callback to print receipt
 * @returns {JSX.Element} Payment component
 */
const POSPayment = ({ 
  totals, 
  customer, 
  onCompletePayment, 
  loading = false, 
  saleCompleted = false,
  onPrintReceipt
}) => {
  const theme = useTheme();
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [receivedAmount, setReceivedAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedReceiptTypes, setSelectedReceiptTypes] = useState({
    CUSTOMER: true,
    OFFICE: false,
    OFFICE_WITH_SIGNATURE: false
  });

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Calculate change amount for cash payments
  const calculateChange = () => {
    if (!receivedAmount || paymentMethod !== 'CASH') return 0;
    const received = parseFloat(receivedAmount);
    return received > totals.total ? received - totals.total : 0;
  };

  // Handle payment submission
  const handleSubmitPayment = () => {
    // Validate received amount for cash payments
    if (paymentMethod === 'CASH') {
      const received = parseFloat(receivedAmount || 0);
      if (isNaN(received) || received < totals.total) {
        // Don't proceed if amount is insufficient 
        return;
      }
    }
    
    // Prepare payment data
    const paymentData = {
      method: paymentMethod,
      amount: totals.total,
      receivedAmount: paymentMethod === 'CASH' ? parseFloat(receivedAmount) : totals.total,
      changeAmount: calculateChange(),
      notes: notes
    };
    
    // Call the parent callback
    onCompletePayment(paymentData);
  };

  // Handle receipt type change
  const handleReceiptTypeChange = (event) => {
    setSelectedReceiptTypes({
      ...selectedReceiptTypes,
      [event.target.name]: event.target.checked
    });
  };

  // Handle print receipt
  const handlePrintReceipt = () => {
    // Get selected receipt types
    const types = Object.entries(selectedReceiptTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([type]) => type);
    
    // Call parent callback with selected types
    if (types.length > 0) {
      onPrintReceipt(types);
    }
  };

  // Payment method icon mapping
  const paymentIcons = {
    CASH: <CashIcon />,
    CREDIT_CARD: <CreditCardIcon />,
    CHEQUE: <ChequeIcon />,
    PAYNOW: <PaymentIcon />,
    CREDIT: <ReceiptIcon />
  };

  return (
    <Box sx={{ width: '100%' }}>
      {saleCompleted ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <SuccessIcon sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Payment Successful!
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            The sale has been completed successfully.
          </Typography>
          
          {/* Receipt Type Selection */}
          <Card variant="outlined" sx={{ mb: 3, mt: 2, textAlign: 'left' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Select Receipt Type(s)
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedReceiptTypes.CUSTOMER}
                      onChange={handleReceiptTypeChange}
                      name="CUSTOMER"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                      For Customer
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedReceiptTypes.OFFICE}
                      onChange={handleReceiptTypeChange}
                      name="OFFICE"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
                      For Office
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedReceiptTypes.OFFICE_WITH_SIGNATURE}
                      onChange={handleReceiptTypeChange}
                      name="OFFICE_WITH_SIGNATURE"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AssignmentIcon sx={{ mr: 1, fontSize: 20 }} />
                      For Office with Customer Signature
                    </Box>
                  }
                />
              </FormGroup>
            </CardContent>
          </Card>
          
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            sx={{ mt: 2 }}
            onClick={handlePrintReceipt}
            disabled={!Object.values(selectedReceiptTypes).some(Boolean)}
          >
            Print Selected Receipts
          </Button>
        </Box>
      ) : (
        <>
          {/* Customer Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Customer Information
            </Typography>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                {customer ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Name
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {customer.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Phone
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {customer.phone || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Email
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {customer.email || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body1" align="center" color="textSecondary">
                    No customer selected
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Sale Summary */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Sale Summary
            </Typography>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Subtotal
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" align="right">
                      {formatCurrency(totals.subtotal)}
                    </Typography>
                  </Grid>
                  
                  {/* Discount */}
                  {totals.discount > 0 && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Discount
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right" color="error">
                          -{formatCurrency(totals.discount)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Tax
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" align="right">
                      {formatCurrency(totals.taxAmount)}
                    </Typography>
                  </Grid>
                  
                  {/* Additional Charges */}
                  {totals.additionalCharges > 0 && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Additional Charges
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right">
                          {formatCurrency(totals.additionalCharges)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Total
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" fontWeight="bold" align="right">
                      {formatCurrency(totals.total)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Payment Method Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Payment Method
            </Typography>
            <FormControl component="fieldset" disabled={loading}>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                row
              >
                <FormControlLabel
                  value="CASH"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CashIcon sx={{ mr: 1 }} />
                      Cash
                    </Box>
                  }
                />
                <FormControlLabel
                  value="CREDIT_CARD"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CreditCardIcon sx={{ mr: 1 }} />
                      Credit Card
                    </Box>
                  }
                />
                <FormControlLabel
                  value="CHEQUE"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ChequeIcon sx={{ mr: 1 }} />
                      Cheque
                    </Box>
                  }
                />
                <FormControlLabel
                  value="PAYNOW"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PaymentIcon sx={{ mr: 1 }} />
                      PayNow
                    </Box>
                  }
                />
                <FormControlLabel
                  value="CREDIT"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ReceiptIcon sx={{ mr: 1 }} />
                      Credit
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Cash Payment Details */}
          {paymentMethod === 'CASH' && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Amount Received"
                    variant="outlined"
                    fullWidth
                    value={receivedAmount}
                    onChange={(e) => setReceivedAmount(e.target.value)}
                    type="number"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    error={parseFloat(receivedAmount || 0) < totals.total}
                    helperText={parseFloat(receivedAmount || 0) < totals.total ? 'Amount is less than total' : ''}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Change"
                    variant="outlined"
                    fullWidth
                    value={formatCurrency(calculateChange())}
                    InputProps={{
                      readOnly: true,
                    }}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Notes */}
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Sale Notes"
              multiline
              rows={2}
              variant="outlined"
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this sale..."
              disabled={loading}
            />
          </Box>

          {/* Submit Button */}
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleSubmitPayment}
              startIcon={loading ? null : paymentIcons[paymentMethod]}
              disabled={
                loading ||
                (paymentMethod === 'CASH' && parseFloat(receivedAmount || 0) < totals.total)
              }
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Complete Payment'
              )}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default POSPayment; 